/**
 * Connection reference validator for Power Automate flows
 */

import type {
  FlowDefinition,
  ActionDefinition,
  TriggerDefinition,
  ConnectionReference,
} from "../api/types.js";
import type { ValidationError } from "./schema-validator.js";
import { displayValue } from "../utils/format.js";

/**
 * Connection validation result
 */
export interface ConnectionValidationResult {
  valid: boolean;
  errors: ValidationError[];
  requiredConnections: Set<string>;
  providedConnections: Set<string>;
  missingConnections: Set<string>;
  unusedConnections: Set<string>;
}

/**
 * Extract connection references used in an action
 */
function extractConnectionsFromAction(action: ActionDefinition): Set<string> {
  const connections = new Set<string>();

  if (action.type === "OpenApiConnection" || action.type === "OpenApiConnectionWebhook") {
    const inputs = action.inputs;
    if (inputs?.host) {
      const host = inputs.host as Record<string, unknown>;
      if (typeof host.connectionName === "string") {
        connections.add(host.connectionName);
      }
    }
  }

  // Check nested actions
  if (action.actions) {
    for (const subAction of Object.values(action.actions)) {
      for (const conn of extractConnectionsFromAction(subAction)) {
        connections.add(conn);
      }
    }
  }

  if (action.else?.actions) {
    for (const subAction of Object.values(action.else.actions)) {
      for (const conn of extractConnectionsFromAction(subAction)) {
        connections.add(conn);
      }
    }
  }

  if (action.cases) {
    for (const caseData of Object.values(action.cases)) {
      if (caseData.actions) {
        for (const subAction of Object.values(caseData.actions)) {
          for (const conn of extractConnectionsFromAction(subAction)) {
            connections.add(conn);
          }
        }
      }
    }
  }

  if (action.default?.actions) {
    for (const subAction of Object.values(action.default.actions)) {
      for (const conn of extractConnectionsFromAction(subAction)) {
        connections.add(conn);
      }
    }
  }

  return connections;
}

/**
 * Extract connection references used in a trigger
 */
function extractConnectionsFromTrigger(trigger: TriggerDefinition): Set<string> {
  const connections = new Set<string>();

  if (trigger.type === "OpenApiConnection" || trigger.type === "OpenApiConnectionWebhook") {
    const inputs = trigger.inputs;
    if (inputs?.host) {
      const host = inputs.host as Record<string, unknown>;
      if (typeof host.connectionName === "string") {
        connections.add(host.connectionName);
      }
    }
  }

  return connections;
}

/**
 * Extract all connection references used in a flow definition
 */
export function extractRequiredConnections(definition: FlowDefinition): Set<string> {
  const connections = new Set<string>();

  // Extract from triggers
  for (const trigger of Object.values(definition.triggers)) {
    for (const conn of extractConnectionsFromTrigger(trigger)) {
      connections.add(conn);
    }
  }

  // Extract from actions
  for (const action of Object.values(definition.actions)) {
    for (const conn of extractConnectionsFromAction(action)) {
      connections.add(conn);
    }
  }

  return connections;
}

/**
 * Validate connection references
 */
export function validateConnections(
  definition: FlowDefinition,
  connectionReferences: Record<string, ConnectionReference>
): ConnectionValidationResult {
  const result: ConnectionValidationResult = {
    valid: true,
    errors: [],
    requiredConnections: new Set(),
    providedConnections: new Set(Object.keys(connectionReferences)),
    missingConnections: new Set(),
    unusedConnections: new Set(),
  };

  // Extract required connections
  result.requiredConnections = extractRequiredConnections(definition);

  // Find missing connections
  for (const conn of result.requiredConnections) {
    if (!result.providedConnections.has(conn)) {
      result.missingConnections.add(conn);
      result.errors.push({
        path: `connectionReferences.${conn}`,
        message: `Missing connection reference: ${conn}`,
        severity: "error",
      });
      result.valid = false;
    }
  }

  // Find unused connections (warning only)
  for (const conn of result.providedConnections) {
    if (!result.requiredConnections.has(conn)) {
      result.unusedConnections.add(conn);
      result.errors.push({
        path: `connectionReferences.${conn}`,
        message: `Unused connection reference: ${conn}`,
        severity: "warning",
      });
    }
  }

  // Validate connection reference structure
  for (const [key, ref] of Object.entries(connectionReferences)) {
    if (!ref.connectionName) {
      result.errors.push({
        path: `connectionReferences.${key}.connectionName`,
        message: "Connection reference missing connectionName",
        severity: "error",
      });
      result.valid = false;
    }

    if (!ref.id) {
      result.errors.push({
        path: `connectionReferences.${key}.id`,
        message: "Connection reference missing id",
        severity: "error",
      });
      result.valid = false;
    }

    if (!ref.source) {
      result.errors.push({
        path: `connectionReferences.${key}.source`,
        message: "Connection reference missing source",
        severity: "warning",
      });
    } else if (ref.source !== "Invoker" && ref.source !== "Embedded") {
      result.errors.push({
        path: `connectionReferences.${key}.source`,
        message: `Invalid source: ${displayValue(ref.source)}. Must be "Invoker" or "Embedded"`,
        severity: "error",
      });
      result.valid = false;
    }
  }

  return result;
}

/**
 * Get connector IDs used in a flow
 */
export function extractConnectorIds(definition: FlowDefinition): Set<string> {
  const connectorIds = new Set<string>();

  function extractFromInputs(inputs: Record<string, unknown> | undefined): void {
    if (!inputs?.host) return;
    const host = inputs.host as Record<string, unknown>;
    if (typeof host.apiId === "string") {
      connectorIds.add(host.apiId);
    }
  }

  // Extract from triggers
  for (const trigger of Object.values(definition.triggers)) {
    if (trigger.type === "OpenApiConnection" || trigger.type === "OpenApiConnectionWebhook") {
      extractFromInputs(trigger.inputs);
    }
  }

  // Recursively extract from actions
  function extractFromAction(action: ActionDefinition): void {
    if (action.type === "OpenApiConnection" || action.type === "OpenApiConnectionWebhook") {
      extractFromInputs(action.inputs);
    }

    // Check nested actions
    const nestedActions = [
      action.actions,
      action.else?.actions,
      action.default?.actions,
    ];

    for (const nested of nestedActions) {
      if (nested) {
        for (const subAction of Object.values(nested)) {
          extractFromAction(subAction);
        }
      }
    }

    if (action.cases) {
      for (const caseData of Object.values(action.cases)) {
        if (caseData.actions) {
          for (const subAction of Object.values(caseData.actions)) {
            extractFromAction(subAction);
          }
        }
      }
    }
  }

  for (const action of Object.values(definition.actions)) {
    extractFromAction(action);
  }

  return connectorIds;
}

/**
 * Check if a flow uses premium connectors
 */
export function usesPremiumConnectors(
  connectionReferences: Record<string, ConnectionReference>
): boolean {
  return Object.values(connectionReferences).some((ref) => ref.tier === "Premium");
}

/**
 * Get premium connectors used in a flow
 */
export function getPremiumConnectors(
  connectionReferences: Record<string, ConnectionReference>
): string[] {
  return Object.entries(connectionReferences)
    .filter(([_, ref]) => ref.tier === "Premium")
    .map(([key, _]) => key);
}

/**
 * Suggest connection reference key from connector ID
 */
export function suggestConnectionKey(connectorId: string): string {
  // Extract the connector name from the full path
  const match = connectorId.match(/\/apis\/(.+)$/);
  if (match && match[1]) {
    return match[1];
  }

  // Fallback: create a sanitized key
  return connectorId.replace(/[^a-zA-Z0-9_]/g, "_").toLowerCase();
}

/**
 * Build a connection reference from minimal info
 */
export function buildConnectionReference(
  connectionName: string,
  connectionId: string,
  options: {
    source?: "Invoker" | "Embedded";
    tier?: "Standard" | "Premium" | "NotSpecified";
  } = {}
): ConnectionReference {
  return {
    connectionName,
    id: connectionId,
    source: options.source ?? "Embedded",
    tier: options.tier ?? "NotSpecified",
  };
}
